import { useState, useEffect } from 'react';
import { HeartPulse, TrendingUp, Users, MessageSquare, Store, Database, Zap, Activity, Globe, Layers, Monitor } from 'lucide-react';
import { Progress } from '../../components/ui/progress';

const API = process.env.REACT_APP_BACKEND_URL;

function MetricCard({ name, value, target, unit, status }) {
  const pct = target > 0 ? Math.min((value / target) * 100, 100) : 0;
  const colors = { green: 'text-emerald-400', amber: 'text-amber-400', red: 'text-red-400' };
  return (
    <div className="bg-[#0E1830] border border-white/[0.08] rounded-xl p-4" data-testid={`metric-${name.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-white/50 text-xs font-medium">{name}</span>
        <span className={`text-xs font-bold ${colors[status] || 'text-white/40'}`}>{status?.toUpperCase()}</span>
      </div>
      <div className="flex items-baseline gap-1 mb-2">
        <span className="text-white text-2xl font-bold">{value}</span>
        <span className="text-white/30 text-sm">{unit} / {target}{unit}</span>
      </div>
      <Progress value={pct} className="h-1.5 bg-white/[0.06]" />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#0E1830] border border-white/[0.08] rounded-xl p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}><Icon className="w-5 h-5 text-white" /></div>
      <div>
        <p className="text-white font-bold text-lg">{typeof value === 'number' ? value.toLocaleString() : value}</p>
        <p className="text-white/40 text-xs">{label}</p>
      </div>
    </div>
  );
}

export default function AdminPilotHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await fetch(`${API}/api/admin/pilot`, { credentials: 'include' }); if (res.ok) setData(await res.json()); } catch (e) { console.error("Pilot data fetch error:", e); }
      finally { setLoading(false); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return <div className="text-white/30 text-center py-16">Failed to load pilot data</div>;

  const t = data.tracking || {};
  const lc = data.layer_counts || {};
  const lu = data.layer_users || {};
  const meta = data.layers_meta || {};
  const sc = data.source_counts || {};
  const pc = data.platform_counts || {};
  const score = data.pilot_readiness_score || 0;
  const scoreColor = score >= 70 ? 'text-emerald-400' : score >= 40 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="admin-pilot-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Pilot Tracking</h1>
          <p className="text-white/40 text-sm mt-1">Real-time metrics for SendPI pilot programme</p>
        </div>
        <div className="text-right">
          <div className={`text-4xl font-bold ${scoreColor}`}>{score}<span className="text-lg text-white/30">/100</span></div>
          <p className="text-white/30 text-xs">Readiness Score</p>
        </div>
      </div>

      {/* Key Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Total Users" value={data.total_users} color="bg-violet-500/20" />
        <StatCard icon={Activity} label="Active Users" value={t.active_users || 0} color="bg-emerald-500/20" />
        <StatCard icon={Zap} label="Total Events" value={lc.total || 0} color="bg-amber-500/20" />
        <StatCard icon={Activity} label="Today's Events" value={t.today_events || 0} color="bg-cyan-500/20" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Database} label="Seeded Casts" value={t.total_seeded_casts || 0} color="bg-purple-500/20" />
        <StatCard icon={Store} label="Packages Added" value={t.store_packages_added || 0} color="bg-cyan-500/20" />
        <StatCard icon={MessageSquare} label="Chat Sessions" value={`${t.active_chats || 0} / ${t.total_chats || 0}`} color="bg-emerald-500/20" />
        <StatCard icon={TrendingUp} label="Google Users" value={t.google_users || 0} color="bg-red-500/20" />
      </div>

      {/* Layer Usage Breakdown */}
      <div className="bg-[#0E1830] border border-white/[0.08] rounded-2xl p-6">
        <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2"><Layers className="w-4 h-4 text-violet-400" /> Usage by PI Layer</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(meta).map(([key, m]) => {
            const count = lc[key] || 0;
            const users = lu[key] || 0;
            const total = lc.total || 1;
            const pct = Math.round((count / total) * 100);
            return (
              <div key={key} className="border border-white/[0.06] rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: m.color }} />
                  <span className="text-white font-bold text-xs">{m.name}</span>
                  <span className="ml-auto text-white/30 text-[10px]">{users} users</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-white text-xl font-bold">{count.toLocaleString()}</span>
                  <span className="text-white/20 text-xs">{pct}%</span>
                </div>
                <Progress value={pct} className="h-1 bg-white/[0.06]" />
              </div>
            );
          })}
        </div>
      </div>

      {/* Source + Platform */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-[#0E1830] border border-white/[0.08] rounded-xl p-5">
          <h2 className="text-white font-bold text-sm mb-3 flex items-center gap-2"><Globe className="w-4 h-4 text-cyan-400" /> Source Split</h2>
          <div className="space-y-3">
            <div className="flex justify-between"><span className="text-white/50 text-xs">Web App</span><span className="text-white font-bold text-sm">{sc.web || 0}</span></div>
            <div className="flex justify-between"><span className="text-white/50 text-xs">Chrome Extension</span><span className="text-white font-bold text-sm">{sc.chrome_extension || 0}</span></div>
          </div>
        </div>
        <div className="bg-[#0E1830] border border-white/[0.08] rounded-xl p-5">
          <h2 className="text-white font-bold text-sm mb-3 flex items-center gap-2"><Monitor className="w-4 h-4 text-amber-400" /> AI Platforms</h2>
          {Object.keys(pc).length === 0 ? (<p className="text-white/20 text-xs">No platform data yet</p>) : (
            <div className="space-y-2">
              {Object.entries(pc).slice(0, 7).map(([name, count]) => (
                <div key={name} className="flex justify-between"><span className="text-white/50 text-xs capitalize">{name}</span><span className="text-white font-bold text-xs">{count}</span></div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pilot Metrics */}
      <div>
        <h2 className="text-white font-bold text-sm mb-3">Pilot Health Metrics</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data.metrics || []).map(m => (<MetricCard key={m.name} {...m} />))}
        </div>
      </div>

      {/* Readiness Breakdown */}
      <div className="bg-[#0E1830] border border-white/[0.08] rounded-2xl p-6">
        <h3 className="text-white font-bold text-sm mb-4">Readiness Breakdown</h3>
        <div className="space-y-3">
          {(data.metrics || []).map(m => {
            const pct = m.target > 0 ? Math.min((m.value / m.target) * 100, 100) : 0;
            return (
              <div key={m.name} className="flex items-center gap-4">
                <span className="text-white/50 text-xs w-44 shrink-0">{m.name}</span>
                <div className="flex-1"><Progress value={pct} className="h-2 bg-white/[0.06]" /></div>
                <span className="text-white/60 text-xs font-bold w-12 text-right">{Math.round(pct)}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
